/*
  Custom Badge artwork upload (Koalaty Commerce, Sept 2026).

  Pairs with snippets/custom-badge-upload.liquid. Everything is delegated at
  the document level in the capture phase, so it also works inside Impulse's
  quick-shop modal and any product section injected after page load.

  What it does:
    - Opens / closes the upload panel when the Badge option changes.
    - Enables the file + notes inputs only while the panel is open, so a
      non-custom variant never submits an empty file property.
    - Validates type and size client-side (Shopify caps uploads at 20 MB).
    - Shows a live "+$X added to the price above" line and appends the same
      delta to the Custom option's label, computed from the product's own
      variant JSON so it tracks real prices.
    - Blocks Add to cart when the Custom value is selected and no file is
      attached. Runs before theme.js's own submit handler.
    - Swaps the express checkout buttons for a one-line note while Custom is
      selected (the CSS hides the buttons; accelerated checkout cannot carry
      a file upload, so Add to cart is the only path that delivers artwork).
*/
(function () {
  'use strict';

  var MAX_BYTES = 20 * 1024 * 1024;
  var ALLOWED_TYPES = { 'image/png': 1, 'image/jpeg': 1, 'image/svg+xml': 1, 'application/pdf': 1 };
  var ALLOWED_EXT = ['png', 'jpg', 'jpeg', 'svg', 'pdf'];
  var IMAGE_EXT = ['png', 'jpg', 'jpeg', 'svg', 'gif', 'webp'];
  var OPTION_KEYS = ['option1', 'option2', 'option3'];

  var STR = {
    required: 'Add your badge artwork before adding this to your cart.',
    type: 'That file type is not supported. Use PNG, JPG, SVG, or PDF.',
    size: 'That file is too large. Keep it under 20 MB.',
    priceNote: ' added to the price above'
  };

  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- helpers ---------- */

  function money(cents) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, theme.settings && theme.settings.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function formatBytes(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  function extOf(name) {
    var parts = (name || '').split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
  }

  function sectionOf(el) {
    return el && el.closest ? el.closest('[data-section-type="product"]') : null;
  }

  function panelsIn(section) {
    return section ? section.querySelectorAll('[data-custom-badge]') : [];
  }

  function fileInput(panel) {
    return panel.querySelector('[data-custom-badge-file]');
  }

  function currentValue(section, optionIndex) {
    var select = section.querySelector('select[data-index="' + optionIndex + '"]');
    if (select) return select.value;
    var radio = section.querySelector('input[type="radio"][data-index="' + optionIndex + '"]:checked');
    return radio ? radio.value : '';
  }

  function isCustomValue(panel, value) {
    return (value || '').toLowerCase().indexOf(panel.dataset.match) !== -1;
  }

  function variantsOf(section) {
    var holder = section.querySelector('[data-variant-json]');
    if (!holder) return [];
    try {
      return JSON.parse(holder.textContent || holder.value || '[]');
    } catch (e) {
      return [];
    }
  }

  /* Price difference between the cheapest Custom variant and the cheapest
     non-Custom variant that share the currently selected other options. */
  function priceDelta(section, panel) {
    var variants = variantsOf(section);
    if (!variants.length) return null;

    var key = panel.dataset.optionIndex;
    var selected = {};
    OPTION_KEYS.forEach(function (k) {
      if (k === key) return;
      var v = currentValue(section, k);
      if (v) selected[k] = v;
    });

    var customMin = null;
    var baseMin = null;
    variants.forEach(function (variant) {
      for (var k in selected) {
        if (variant[k] !== selected[k]) return;
      }
      var price = Number(variant.price);
      if (isCustomValue(panel, variant[key])) {
        if (customMin === null || price < customMin) customMin = price;
      } else {
        if (baseMin === null || price < baseMin) baseMin = price;
      }
    });

    if (customMin === null || baseMin === null) return null;
    return customMin - baseMin;
  }

  /* Append "(+$200.00)" to the Custom option's label in the picker. */
  function labelSuffix(section, panel, delta) {
    var key = panel.dataset.optionIndex;
    var suffix = delta && delta > 0 ? ' (+' + money(delta) + ')' : '';

    section.querySelectorAll('select[data-index="' + key + '"] option').forEach(function (opt) {
      if (!isCustomValue(panel, opt.value)) return;
      if (!opt.dataset.cbLabel) opt.dataset.cbLabel = opt.textContent.trim();
      opt.textContent = opt.dataset.cbLabel + suffix;
    });

    section.querySelectorAll('input[type="radio"][data-index="' + key + '"]').forEach(function (radio) {
      if (!isCustomValue(panel, radio.value)) return;
      var label = section.querySelector('label[for="' + radio.id + '"]');
      if (!label) return;
      if (!label.dataset.cbLabel) label.dataset.cbLabel = label.textContent.trim();
      label.textContent = label.dataset.cbLabel + suffix;
    });
  }

  /* ---------- panel state ---------- */

  function showError(panel, message) {
    var errorEl = panel.querySelector('[data-custom-badge-error]');
    var drop = panel.querySelector('[data-custom-badge-drop]');
    if (!errorEl) return;
    if (message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
      if (drop) drop.classList.add('has-error');
    } else {
      errorEl.textContent = '';
      errorEl.hidden = true;
      if (drop) drop.classList.remove('has-error');
    }
  }

  function renderFile(panel, file) {
    var empty = panel.querySelector('[data-custom-badge-empty]');
    var filled = panel.querySelector('[data-custom-badge-filled]');
    var thumb = panel.querySelector('[data-custom-badge-thumb]');
    var nameEl = panel.querySelector('[data-custom-badge-name]');
    var sizeEl = panel.querySelector('[data-custom-badge-size]');

    if (panel._cbObjectUrl) {
      URL.revokeObjectURL(panel._cbObjectUrl);
      panel._cbObjectUrl = null;
    }
    if (thumb) thumb.innerHTML = '';

    if (!file) {
      if (empty) empty.hidden = false;
      if (filled) filled.hidden = true;
      return;
    }

    var ext = extOf(file.name);
    if (thumb) {
      if (IMAGE_EXT.indexOf(ext) !== -1 || /^image\//.test(file.type)) {
        var img = document.createElement('img');
        panel._cbObjectUrl = URL.createObjectURL(file);
        img.src = panel._cbObjectUrl;
        img.alt = '';
        thumb.appendChild(img);
      } else {
        var tag = document.createElement('span');
        tag.className = 'custom-badge__thumb-ext';
        tag.textContent = (ext || 'file').toUpperCase();
        thumb.appendChild(tag);
      }
    }
    if (nameEl) nameEl.textContent = file.name;
    if (sizeEl) sizeEl.textContent = formatBytes(file.size);
    if (empty) empty.hidden = true;
    if (filled) filled.hidden = false;
  }

  function validate(file) {
    var ext = extOf(file.name);
    var typeOk = ALLOWED_TYPES[file.type] || ALLOWED_EXT.indexOf(ext) !== -1;
    if (!typeOk) return STR.type;
    if (file.size > MAX_BYTES) return STR.size;
    return null;
  }

  function clearFile(panel) {
    var input = fileInput(panel);
    if (input) input.value = '';
    renderFile(panel, null);
    showError(panel, null);
  }

  function acceptFiles(panel, files) {
    var input = fileInput(panel);
    if (!input || !files || !files.length) return;
    var file = files[0];
    var error = validate(file);
    if (error) {
      input.value = '';
      renderFile(panel, null);
      showError(panel, error);
      return;
    }
    if (input.files !== files) {
      try {
        var dt = new DataTransfer();
        dt.items.add(file);
        input.files = dt.files;
      } catch (e) {
        /* Very old browsers: fall back to the native picker only. */
        showError(panel, STR.required);
        return;
      }
    }
    showError(panel, null);
    renderFile(panel, file);
  }

  /* Express checkout (Shop Pay, More payment options) is hidden by CSS while
     a Custom value is selected, because accelerated checkout cannot carry a
     file upload. Put a one-line note where the buttons were so the change
     reads as intentional instead of broken. */
  function expressNote(panel, form, open) {
    if (!form) return;
    var text = panel.dataset.expressNote;
    var button = form.querySelector('.shopify-payment-button');
    var note = form.querySelector('[data-custom-badge-express]');
    if (!note) {
      if (!open || !button || !text) return;
      note = document.createElement('p');
      note.className = 'custom-badge__express';
      note.setAttribute('data-custom-badge-express', '');
      note.textContent = text;
      button.insertAdjacentElement('afterend', note);
    }
    note.hidden = !open;
  }

  function setOpen(panel, open) {
    var wasOpen = !panel.hidden;
    panel.hidden = !open;
    panel.classList.toggle('is-open', open);
    panel.querySelectorAll('input, textarea').forEach(function (field) {
      field.disabled = !open;
    });
    var form = document.getElementById(panel.dataset.formId);
    if (form) form.classList.toggle('custom-badge-active', open);
    expressNote(panel, form, open);
    if (wasOpen && !open) clearFile(panel);
  }

  function update(section) {
    if (!section) return;
    panelsIn(section).forEach(function (panel) {
      var value = currentValue(section, panel.dataset.optionIndex);
      setOpen(panel, isCustomValue(panel, value));

      var delta = priceDelta(section, panel);
      var priceEl = panel.querySelector('[data-custom-badge-price]');
      if (priceEl) {
        if (delta && delta > 0) {
          priceEl.textContent = '+' + money(delta) + STR.priceNote;
          priceEl.hidden = false;
        } else {
          priceEl.hidden = true;
        }
      }
      labelSuffix(section, panel, delta);
      panel.dataset.cbInit = 'true';
    });
  }

  function initAll() {
    document.querySelectorAll('[data-custom-badge]:not([data-cb-init])').forEach(function (panel) {
      update(sectionOf(panel));
    });
  }

  /* ---------- events (document level, capture phase) ---------- */

  document.addEventListener('change', function (evt) {
    var target = evt.target;
    if (!target || !target.matches) return;

    if (target.matches('[data-custom-badge-file]')) {
      var panel = target.closest('[data-custom-badge]');
      if (panel) acceptFiles(panel, target.files);
      return;
    }

    if (target.matches('[data-variant-input]')) {
      update(sectionOf(target));
    }
  }, true);

  document.addEventListener('variantChange', function (evt) {
    update(sectionOf(evt.target));
  }, true);

  document.addEventListener('click', function (evt) {
    var btn = evt.target && evt.target.closest ? evt.target.closest('[data-custom-badge-remove]') : null;
    if (!btn) return;
    evt.preventDefault();
    var panel = btn.closest('[data-custom-badge]');
    if (panel) {
      clearFile(panel);
      var input = fileInput(panel);
      if (input) input.focus();
    }
  }, true);

  ['dragenter', 'dragover'].forEach(function (name) {
    document.addEventListener(name, function (evt) {
      var drop = evt.target && evt.target.closest ? evt.target.closest('[data-custom-badge-drop]') : null;
      if (!drop) return;
      evt.preventDefault();
      drop.classList.add('is-dragover');
    }, true);
  });

  document.addEventListener('dragleave', function (evt) {
    var drop = evt.target && evt.target.closest ? evt.target.closest('[data-custom-badge-drop]') : null;
    if (!drop) return;
    if (evt.relatedTarget && drop.contains(evt.relatedTarget)) return;
    drop.classList.remove('is-dragover');
  }, true);

  document.addEventListener('drop', function (evt) {
    var drop = evt.target && evt.target.closest ? evt.target.closest('[data-custom-badge-drop]') : null;
    if (!drop) return;
    evt.preventDefault();
    drop.classList.remove('is-dragover');
    var panel = drop.closest('[data-custom-badge]');
    if (panel && evt.dataTransfer) acceptFiles(panel, evt.dataTransfer.files);
  }, true);

  /* Gate the product form: Custom selected + no file = stop here. */
  document.addEventListener('submit', function (evt) {
    var form = evt.target;
    if (!form || !form.classList || !form.classList.contains('custom-badge-active')) return;
    /* form.id is shadowed by the <select name="id"> inside Shopify product
       forms, so read the attribute instead of the property. */
    var formId = form.getAttribute('id');
    var panel = document.querySelector('[data-custom-badge][data-form-id="' + formId + '"]');
    if (!panel || panel.hidden) return;
    var input = fileInput(panel);
    if (input && input.files && input.files.length) {
      showError(panel, null);
      return;
    }
    evt.preventDefault();
    evt.stopImmediatePropagation();
    showError(panel, STR.required);
    panel.scrollIntoView({ block: 'center', behavior: reducedMotion ? 'auto' : 'smooth' });
    if (input) input.focus({ preventScroll: true });
  }, true);

  /* ---------- boot ---------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Quick-shop modals and other late-loaded product sections. One-shot per
     panel (data-cb-init), so this never loops on its own DOM writes. */
  if (window.MutationObserver) {
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = window.setTimeout(function () {
        pending = null;
        initAll();
      }, 60);
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
